import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plug,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  TestTube,
  Settings,
  Globe,
  Building2,
  MapPin,
  CreditCard,
  Shield,
  User,
  Mail,
  FileText,
  AlertCircle,
  ExternalLink,
  Search,
  Filter,
  Play,
  Trash2,
  Plus,
  Workflow,
  Zap,
  MessageSquare,
  Users
} from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { LoadingSpinner } from '../../components/ui/LoadingSpinner'
import { VectrLogo } from '../../components/ui/VectrLogo'
import { getVendorIcon } from '../../components/ui/VendorIcons'
import { useAuth } from '../../contexts/AuthContext'
import { vectrApi, VectrWorkflow } from '../../services/api/vectr'
import toast from 'react-hot-toast'
import { useConfirm } from '../../contexts/ConfirmProvider'

const VECTR_API_BASE_URL = import.meta.env.VITE_VECTR_API_URL || 'https://api.vectr.ai/v1'

interface IntegrationWorkflow {
  id: string
  name: string
  description: string
  category: 'property' | 'payment' | 'identity' | 'communication' | 'document' | 'authentication' | 'automation'
  icon: React.ComponentType<{ className?: string }>
  vendor?: string
  phase: 'initial' | 'exchange' | 'acquisition' | 'cross-cutting'
  trigger: string
  cadence: string
  vectrWorkflowId?: string
  vectrWorkflow?: VectrWorkflow
  status: 'active' | 'inactive' | 'error' | 'pending'
  widgetConfig?: {
    enabled: boolean
    widgetId?: string
    embedCode?: string
  }
}

export const AdminIntegrationsPage: React.FC = () => {
  const { user: currentUser } = useAuth()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const [integrations, setIntegrations] = useState<IntegrationWorkflow[]>([])
  const [vectrWorkflows, setVectrWorkflows] = useState<VectrWorkflow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isTesting, setIsTesting] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newWorkflowPrompt, setNewWorkflowPrompt] = useState('')
  const [creatingWorkflow, setCreatingWorkflow] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string>('')
  const [phaseFilter, setPhaseFilter] = useState<string>('')
  const [vectrUnavailable, setVectrUnavailable] = useState(false)

  useEffect(() => {
    if (!currentUser?.isAdmin) {
      navigate('/app')
      return
    }
    const initialize = async () => {
      await loadVectrWorkflows()
      await loadIntegrations()
    }
    initialize()
  }, [currentUser, navigate])

  const loadVectrWorkflows = async () => {
    try {
      const workflows = await vectrApi.getWorkflows()
      setVectrWorkflows(workflows)
      setVectrUnavailable(false)
    } catch (error) {
      console.error('Vectr getWorkflows failed:', error)
      setVectrUnavailable(true)
      setVectrWorkflows([])
    }
  }

  const loadIntegrations = async () => {
    try {
      if (integrations.length === 0) {
        setIsLoading(true)
      }
      
      // Define all integrations/workflows from WORKFLOWS.md
      const allIntegrations: IntegrationWorkflow[] = [
        {
          id: 'address-autocomplete',
          name: 'Address Autocomplete',
          description: 'Provides intellisense for address; fills street, city, state, zip using Google Places API',
          category: 'property',
          icon: MapPin,
          vendor: 'Google Places',
          phase: 'initial',
          trigger: 'Event: on input keystrokes (>=3 chars)',
          cadence: 'Event: on selection populate fields',
          vectrWorkflowId: 'google-places-autocomplete',
          status: 'active',
          widgetConfig: {
            enabled: true,
            widgetId: 'google-places-widget',
            embedCode: '<script src="https://maps.googleapis.com/maps/api/js?key=YOUR_KEY&libraries=places"></script>'
          }
        },
        {
          id: 'property-enrichment-relinquished',
          name: 'Property Data Enrichment (Relinquished)',
          description: 'Fetches image, price, beds, baths, sqft, links from Zillow; first image selection',
          category: 'property',
          icon: Building2,
          vendor: 'Zillow',
          phase: 'initial',
          trigger: 'Event: on address saved',
          cadence: 'Schedule: daily 02:00 refresh',
          vectrWorkflowId: 'zillow-property-enrichment',
          status: 'active',
          widgetConfig: {
            enabled: true,
            widgetId: 'zillow-widget',
            embedCode: '<script src="https://widgets.zillow.com/embed.js"></script>'
          }
        },
        {
          id: 'property-enrichment-replacement',
          name: 'Identified Property Data Pull (Replacement)',
          description: 'Enriches identified properties with latest market data/images from Zillow',
          category: 'property',
          icon: Building2,
          vendor: 'Zillow',
          phase: 'exchange',
          trigger: 'Event: on identification save',
          cadence: 'Schedule: daily 03:00 refresh',
          status: 'active'
        },
        {
          id: 'stripe-payments',
          name: 'Stripe Payments',
          description: 'Payment processing and subscription management',
          category: 'payment',
          icon: CreditCard,
          vendor: 'Stripe',
          phase: 'cross-cutting',
          trigger: 'Event: on payment action',
          cadence: 'Real-time',
          status: 'active',
          widgetConfig: {
            enabled: true,
            widgetId: 'stripe-checkout-widget',
            embedCode: '<script src="https://js.stripe.com/v3/"></script>'
          }
        },
        {
          id: 'withpersona-verification',
          name: 'WithPersona Identity Verification',
          description: 'KYC and identity verification services',
          category: 'identity',
          icon: Shield,
          vendor: 'WithPersona',
          phase: 'initial',
          trigger: 'Event: on verification request',
          cadence: 'Real-time',
          status: 'active',
          widgetConfig: {
            enabled: true,
            widgetId: 'persona-verification-widget',
            embedCode: '<script src="https://cdn.withpersona.com/web/persona.js"></script>'
          }
        },
        {
          id: 'apple-sso',
          name: 'Apple Sign-In',
          description: 'Single Sign-On authentication via Apple ID',
          category: 'authentication',
          icon: User,
          phase: 'initial',
          trigger: 'Event: on sign-in click',
          cadence: 'Real-time',
          status: 'pending'
        },
        {
          id: 'google-sso',
          name: 'Google Sign-In',
          description: 'Single Sign-On authentication via Google',
          category: 'authentication',
          icon: User,
          phase: 'initial',
          trigger: 'Event: on sign-in click',
          cadence: 'Real-time',
          status: 'pending'
        },
        {
          id: 'sendgrid-email',
          name: 'SendGrid Email',
          description: 'Transactional email delivery service',
          category: 'communication',
          icon: Mail,
          vendor: 'SendGrid',
          phase: 'cross-cutting',
          trigger: 'Event: on email send',
          cadence: 'Real-time',
          status: 'active'
        },
        {
          id: 'm365-email-document-upload',
          name: 'M365: Document Upload Email',
          description: 'Send email notifications via Microsoft 365/Outlook API when documents are uploaded to the platform',
          category: 'communication',
          icon: Mail,
          vendor: 'Microsoft 365',
          phase: 'cross-cutting',
          trigger: 'Event: on document upload',
          cadence: 'Real-time with retry on failure',
          status: 'pending',
          widgetConfig: {
            enabled: true,
            widgetId: 'm365-document-upload-email',
            embedCode: 'Microsoft Graph API integration'
          }
        },
        {
          id: 'm365-email-deadline-reminder',
          name: 'M365: Deadline Reminder Email',
          description: 'Send deadline reminder emails via Microsoft 365/Outlook API for 45-day and 180-day deadlines',
          category: 'communication',
          icon: Mail,
          vendor: 'Microsoft 365',
          phase: 'cross-cutting',
          trigger: 'Event: on deadline reminder (45-day, 180-day)',
          cadence: 'Schedule: daily 08:00, escalations at -10/-5/-1 days',
          status: 'pending',
          widgetConfig: {
            enabled: true,
            widgetId: 'm365-deadline-reminder-email',
            embedCode: 'Microsoft Graph API integration'
          }
        },
        {
          id: 'm365-email-exchange-milestone',
          name: 'M365: Exchange Milestone Email',
          description: 'Send milestone notification emails via Microsoft 365/Outlook API when exchange milestones are reached',
          category: 'communication',
          icon: Mail,
          vendor: 'Microsoft 365',
          phase: 'cross-cutting',
          trigger: 'Event: on exchange milestone reached',
          cadence: 'Real-time with retry on failure',
          status: 'pending',
          widgetConfig: {
            enabled: true,
            widgetId: 'm365-exchange-milestone-email',
            embedCode: 'Microsoft Graph API integration'
          }
        },
        {
          id: 'm365-email-user-action',
          name: 'M365: User Action Email',
          description: 'Send confirmation and notification emails via Microsoft 365/Outlook API for user actions (login, profile updates, etc.)',
          category: 'communication',
          icon: Mail,
          vendor: 'Microsoft 365',
          phase: 'cross-cutting',
          trigger: 'Event: on user action (login, profile update, exchange creation)',
          cadence: 'Real-time with retry on failure',
          status: 'pending',
          widgetConfig: {
            enabled: true,
            widgetId: 'm365-user-action-email',
            embedCode: 'Microsoft Graph API integration'
          }
        },
        {
          id: 'teams-exchange-event-notification',
          name: 'Teams: Exchange Event Notification',
          description: 'Send notifications to Microsoft Teams channels when exchange events occur (creation, status change, completion)',
          category: 'communication',
          icon: MessageSquare,
          vendor: 'Microsoft Teams',
          phase: 'cross-cutting',
          trigger: 'Event: on exchange event (creation, status change, completion)',
          cadence: 'Real-time with webhook delivery',
          status: 'pending',
          widgetConfig: {
            enabled: true,
            widgetId: 'teams-exchange-event-notification',
            embedCode: 'Microsoft Teams Webhook integration'
          }
        },
        {
          id: 'teams-document-completion-alert',
          name: 'Teams: Document Completion Alert',
          description: 'Send alerts to Microsoft Teams channels when documents are completed or verified',
          category: 'communication',
          icon: MessageSquare,
          vendor: 'Microsoft Teams',
          phase: 'cross-cutting',
          trigger: 'Event: on document completion or verification',
          cadence: 'Real-time with webhook delivery',
          status: 'pending',
          widgetConfig: {
            enabled: true,
            widgetId: 'teams-document-completion-alert',
            embedCode: 'Microsoft Teams Webhook integration'
          }
        },
        {
          id: 'teams-deadline-warning',
          name: 'Teams: Deadline Warning',
          description: 'Send deadline warnings to Microsoft Teams channels for approaching 45-day and 180-day deadlines',
          category: 'communication',
          icon: MessageSquare,
          vendor: 'Microsoft Teams',
          phase: 'cross-cutting',
          trigger: 'Event: on deadline warning (approaching 45-day or 180-day deadline)',
          cadence: 'Schedule: daily 08:00, escalations at -10/-5/-1 days',
          status: 'pending',
          widgetConfig: {
            enabled: true,
            widgetId: 'teams-deadline-warning',
            embedCode: 'Microsoft Teams Webhook integration'
          }
        },
        {
          id: 'teams-workflow-status-change',
          name: 'Teams: Workflow Status Change',
          description: 'Send notifications to Microsoft Teams channels when workflow status changes (published, failed, provisioning)',
          category: 'communication',
          icon: MessageSquare,
          vendor: 'Microsoft Teams',
          phase: 'cross-cutting',
          trigger: 'Event: on workflow status change',
          cadence: 'Real-time with webhook delivery',
          status: 'pending',
          widgetConfig: {
            enabled: true,
            widgetId: 'teams-workflow-status-change',
            embedCode: 'Microsoft Teams Webhook integration'
          }
        },
        {
          id: 'teams-channel-sync-exchange-creation',
          name: 'Teams: Channel Sync - Exchange Creation',
          description: 'Automatically create dedicated Microsoft Teams channels when exchanges are created and sync initial setup',
          category: 'communication',
          icon: Users,
          vendor: 'Microsoft Teams',
          phase: 'cross-cutting',
          trigger: 'Event: on exchange creation',
          cadence: 'Real-time channel creation',
          status: 'pending'
        },
        {
          id: 'teams-channel-sync-document-status',
          name: 'Teams: Channel Sync - Document Status',
          description: 'Sync document status updates to dedicated Microsoft Teams channels per exchange',
          category: 'communication',
          icon: Users,
          vendor: 'Microsoft Teams',
          phase: 'cross-cutting',
          trigger: 'Event: on document status change',
          cadence: 'Real-time channel updates',
          status: 'pending'
        },
        {
          id: 'teams-channel-sync-milestone',
          name: 'Teams: Channel Sync - Milestone Reached',
          description: 'Post milestone updates to dedicated Microsoft Teams channels when exchange milestones are reached',
          category: 'communication',
          icon: Users,
          vendor: 'Microsoft Teams',
          phase: 'cross-cutting',
          trigger: 'Event: on milestone reached',
          cadence: 'Real-time channel updates',
          status: 'pending'
        },
        {
          id: 'docusign-esignature',
          name: 'DocuSign E-Signature',
          description: 'Electronic signature and document management',
          category: 'document',
          icon: FileText,
          vendor: 'DocuSign',
          phase: 'initial',
          trigger: 'Event: on Sign action',
          cadence: 'Schedule: reminder daily at 10:00 for 3 days if pending',
          status: 'inactive'
        },
        {
          id: 'document-verification-merge',
          name: 'Document Verification Merge',
          description: 'Merges seed verified docs with uploads; sets statuses; enables download',
          category: 'document',
          icon: FileText,
          phase: 'initial',
          trigger: 'Event: on upload/verify',
          cadence: 'Schedule: nightly 01:00 recompute statuses',
          vectrWorkflowId: 'document-verification',
          status: 'active'
        },
        {
          id: 'wizard-state-autosave',
          name: 'Wizard State Autosave',
          description: 'Saves wizard progress to localStorage and D1 when enabled',
          category: 'automation',
          icon: Zap,
          phase: 'initial',
          trigger: 'Event: on field change (debounced 5s)',
          cadence: 'Event: on explicit Save',
          status: 'active'
        },
        {
          id: 'progress-computation',
          name: 'Progress Computation',
          description: 'Computes % complete from saved fields and required docs only',
          category: 'automation',
          icon: Zap,
          phase: 'initial',
          trigger: 'Event: on save',
          cadence: 'Schedule: nightly 23:59 snapshot for analytics',
          status: 'active'
        },
        {
          id: '45-day-deadline-watcher',
          name: '45-Day Identification Deadline Watcher',
          description: 'Tracks 45-day deadline; sends escalating reminders',
          category: 'automation',
          icon: Clock,
          phase: 'exchange',
          trigger: 'Schedule: daily 08:00',
          cadence: 'Escalations at -10/-5/-1 days',
          status: 'active'
        },
        {
          id: '180-day-deadline-guard',
          name: '180-Day Deadline Guard',
          description: 'High-frequency checks near deadline; alerts and safety rails',
          category: 'automation',
          icon: Clock,
          phase: 'acquisition',
          trigger: 'Schedule: hourly during last 3 days',
          cadence: 'Every 15 min during last 6 hours',
          status: 'active'
        }
      ]

      // Match Vectr workflows to integrations
      const integrationsWithVectr = allIntegrations.map(integration => {
        const vectrWorkflow = vectrWorkflows.find(w => 
          w.workflow_id === integration.vectrWorkflowId || 
          w.title?.toLowerCase().includes(integration.name.toLowerCase())
        )
        return {
          ...integration,
          vectrWorkflow,
          status: vectrWorkflow ? 
            (vectrWorkflow.workflow_status === 'published' ? 'active' as const :
             vectrWorkflow.workflow_status === 'failed' ? 'error' as const :
             vectrWorkflow.workflow_status === 'provisioning' ? 'pending' as const : 'inactive' as const) :
            integration.status
        }
      })

      setIntegrations(integrationsWithVectr)
    } catch (error) {
      console.error('Failed to load integrations:', error)
      toast.error('Failed to load integrations')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateVectrWorkflow = async (integrationId?: string) => {
    try {
      setCreatingWorkflow(true)
      
      let prompt = ''
      if (integrationId && integrationId !== 'custom') {
        const integration = integrations.find(i => i.id === integrationId)
        if (!integration) return
        prompt = `Create a workflow for: ${integration.name}. ${integration.description}. Trigger: ${integration.trigger}. Cadence: ${integration.cadence}`
      } else {
        prompt = newWorkflowPrompt.trim()
        if (!prompt) {
          toast.error('Please enter a workflow prompt')
          return
        }
      }
      
      // Mock - replace with actual API call
      // const workflow = await vectrApi.createWorkflow('acc-1', prompt)
      
      toast.success(`Creating Vectr workflow...`)
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Reload to get the new workflow
      await loadVectrWorkflows()
      await loadIntegrations()
      setShowCreateModal(false)
      setNewWorkflowPrompt('')
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create Vectr workflow')
    } finally {
      setCreatingWorkflow(false)
    }
  }

  const handlePublishWorkflow = async (integrationId: string) => {
    const integration = integrations.find(i => i.id === integrationId)
    if (!integration?.vectrWorkflow) return

    try {
      // Mock - replace with actual API call
      // await vectrApi.publishWorkflow('acc-1', integration.vectrWorkflow.id)
      toast.success(`Publishing ${integration.name} workflow to Vectr.ai...`)
      await loadVectrWorkflows()
      await loadIntegrations()
    } catch (error: any) {
      toast.error('Failed to publish workflow')
    }
  }

  const handleRedeployWorkflow = async (integrationId: string) => {
    const integration = integrations.find(i => i.id === integrationId)
    if (!integration?.vectrWorkflow) return

    try {
      // Mock - replace with actual API call
      // await vectrApi.redeployAgent('acc-1', integration.vectrWorkflow.id)
      toast.success(`Redeploying ${integration.name} workflow agent...`)
      await loadVectrWorkflows()
      await loadIntegrations()
    } catch (error: any) {
      toast.error('Failed to redeploy workflow')
    }
  }

  const handleDeleteWorkflow = async (integrationId: string) => {
    const integration = integrations.find(i => i.id === integrationId)
    if (!integration?.vectrWorkflow) return

    const ok = await confirm({
      title: 'Delete Vectr workflow?',
      description: `This will delete the Vectr workflow for ${integration.name}. This action cannot be undone.`,
      destructive: true,
      icon: Trash2,
      iconColor: 'rose',
    })
    if (!ok) return

    try {
      // Mock - replace with actual API call
      // await vectrApi.deleteWorkflow('acc-1', integration.vectrWorkflow.id)
      toast.success(`Deleted Vectr workflow for ${integration.name}`)
      await loadVectrWorkflows()
      await loadIntegrations()
    } catch (error: any) {
      toast.error('Failed to delete workflow')
    }
  }

  const handleTestIntegration = async (integrationId: string) => {
    try {
      setIsTesting(integrationId)
      const integration = integrations.find(i => i.id === integrationId)
      if (!integration) return

      // Mock test - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      toast.success(`${integration.name} connection test successful`)
    } catch (error: any) {
      const integration = integrations.find(i => i.id === integrationId)
      toast.error(integration?.name + ' connection test failed')
    } finally {
      setIsTesting(null)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </span>
        )
      case 'error':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Error
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
            <XCircle className="h-3 w-3 mr-1" />
            Inactive
          </span>
        )
    }
  }

  const getWorkflowStatusBadge = (workflowStatus?: string) => {
    if (!workflowStatus) return null
    
    switch (workflowStatus) {
      case 'published':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Published on Vectr.ai
          </span>
        )
      case 'publishing':
      case 'publishing_queued':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
            <Clock className="h-3 w-3 mr-1" />
            Publishing...
          </span>
        )
      case 'provisioning':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
            <Zap className="h-3 w-3 mr-1" />
            Provisioning
          </span>
        )
      case 'failed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Failed
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
            <Clock className="h-3 w-3 mr-1" />
            {workflowStatus}
          </span>
        )
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'property':
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
      case 'payment':
        return 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
      case 'identity':
        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
      case 'communication':
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
      case 'document':
        return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'
      case 'authentication':
        return 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400'
      case 'automation':
        return 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400'
      default:
        return 'bg-secondary text-secondary-foreground'
    }
  }

  const getPhaseColor = (phase: string) => {
    switch (phase) {
      case 'initial':
        return 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
      case 'exchange':
        return 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
      case 'acquisition':
        return 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300'
      case 'cross-cutting':
        return 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
      default:
        return 'bg-secondary text-secondary-foreground'
    }
  }

  const filteredIntegrations = integrations.filter(integration => {
    const matchesSearch = !searchQuery || 
      integration.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      integration.description.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = !categoryFilter || integration.category === categoryFilter
    const matchesPhase = !phaseFilter || integration.phase === phaseFilter
    return matchesSearch && matchesCategory && matchesPhase
  })

  if (isLoading && integrations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const categories = ['property', 'payment', 'identity', 'communication', 'document', 'authentication', 'automation']
  const phases = ['initial', 'exchange', 'acquisition', 'cross-cutting']
  const activeCount = integrations.filter(i => i.status === 'active').length
  const vectrConfiguredCount = integrations.filter(i => i.vectrWorkflow).length

  return (
    <div className="space-y-6">
      {vectrUnavailable && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4"
        >
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            Vectr workflow service is currently unavailable. Configured workflow IDs are still editable below.
          </p>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <VectrLogo size="lg" />
            Vectr Integrations & Workflows
          </h1>
          <p className="text-muted-foreground mt-1">
            Workflows are fetched from Vectr widgets. Each workflow's trigger (webhook, schedule, event) is configured in Vectr and synced here.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Workflow
          </Button>
          <Button onClick={() => {
            loadVectrWorkflows()
            loadIntegrations()
          }} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total Integrations</p>
              <p className="text-3xl font-bold text-foreground mt-2">{integrations.length}</p>
            </div>
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Plug className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Vectr Configured</p>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-2">{vectrConfiguredCount}</p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <Workflow className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active</p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">{activeCount}</p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Vectr Workflows</p>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">{vectrWorkflows.length}</p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <VectrLogo size="md" />
            </div>
          </div>
        </Card>
      </div>

      {/* Vectr API Configuration */}
      <Card className="p-6 border-2 border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50/50 to-pink-50/50 dark:from-purple-900/20 dark:to-pink-900/20">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/40 rounded-lg flex-shrink-0 flex items-center justify-center">
            <VectrLogo size="lg" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
              Vectr Widget Integration
              <span className="text-xs px-2 py-1 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-medium">
                Powered by Vectr.ai
              </span>
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              This page displays workflows fetched from Vectr widgets. Each workflow is configured in Vectr with its own trigger (webhook, schedule, event, etc.). The workflow data, trigger configuration, and status are all managed in Vectr and synced here.
            </p>
            <div className="space-y-3">
              <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2 flex items-center gap-2">
                  <Settings className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  Vectr Widget API Configuration
                </h4>
                <div className="space-y-2 text-xs font-mono text-foreground">
                  <div className="flex items-center gap-2">
                    <span className="text-purple-600 dark:text-purple-400">Vectr API Base URL:</span>
                    <code className="px-2 py-1 bg-secondary rounded">
                      {VECTR_API_BASE_URL}
                    </code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-purple-600 dark:text-purple-400">Account ID:</span>
                    <code className="px-2 py-1 bg-secondary rounded">acc-1</code>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-purple-600 dark:text-purple-400">Workflow Widget Endpoint:</span>
                    <code className="px-2 py-1 bg-secondary rounded">
                      https://workflow-agent-{`{workflow_id}`}.workers.vectr.ai
                    </code>
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-purple-200 dark:border-purple-800">
                    <span className="text-purple-600 dark:text-purple-400">Webhook Endpoint:</span>
                    <code className="px-2 py-1 bg-secondary rounded">
                      https://workflow-agent-{`{workflow_id}`}.workers.vectr.ai/webhook
                    </code>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Workflows are fetched from Vectr widgets API. Each workflow's trigger configuration (webhook URL, schedule, event types) is managed in Vectr.
                </p>
              </div>
              <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2 flex items-center gap-2">
                  <Plug className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  Widget Embedding
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  Each workflow can be embedded as a widget using the following pattern:
                </p>
                <pre className="text-xs bg-slate-900 dark:bg-slate-950 text-green-400 p-3 rounded overflow-x-auto">
{`<div id="vectr-widget-{workflow_id}"></div>
<script>
  window.VectrWidget.init({
    workflowId: '{workflow_id}',
    container: '#vectr-widget-{workflow_id}',
    apiKey: 'your-api-key'
  });
</script>`}
                </pre>
              </div>
              <div className="bg-white/60 dark:bg-slate-800/60 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  Trigger Configuration (Configured in Vectr)
                </h4>
                <p className="text-xs text-muted-foreground mb-3">
                  Each workflow's trigger is configured in Vectr. Triggers can be:
                </p>
                <div className="space-y-2 text-xs text-foreground mb-3">
                  <div className="flex items-start gap-2">
                    <span className="text-purple-600 dark:text-purple-400 font-mono">•</span>
                    <span><strong>Webhook:</strong> Platform sends events to Vectr webhook endpoint</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-purple-600 dark:text-purple-400 font-mono">•</span>
                    <span><strong>Schedule:</strong> Cron-based triggers (daily, hourly, etc.) configured in Vectr</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-purple-600 dark:text-purple-400 font-mono">•</span>
                    <span><strong>Event:</strong> Platform events that trigger workflows via webhook</span>
                  </div>
                </div>
                <div className="pt-3 border-t border-purple-200 dark:border-purple-800">
                  <p className="text-xs font-semibold text-foreground mb-2">Example Workflow Triggers:</p>
                  <div className="space-y-2 text-xs text-foreground">
                    <div className="flex items-start gap-2">
                      <span className="text-purple-600 dark:text-purple-400 font-mono">•</span>
                      <span><strong>M365: Document Upload Email</strong> → Webhook: POST /webhook/document-upload</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-purple-600 dark:text-purple-400 font-mono">•</span>
                      <span><strong>M365: Deadline Reminder Email</strong> → Schedule: Daily 08:00 (configured in Vectr)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-purple-600 dark:text-purple-400 font-mono">•</span>
                      <span><strong>Teams: Exchange Event Notification</strong> → Webhook: POST /webhook/exchange-event</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-purple-600 dark:text-purple-400 font-mono">•</span>
                      <span><strong>Teams: Document Completion Alert</strong> → Webhook: POST /webhook/document-complete</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                <AlertCircle className="h-4 w-4 text-purple-600 dark:text-purple-400 flex-shrink-0" />
                <span className="text-muted-foreground">
                  Workflows are automatically synced from Vectr.ai. Changes made in Vectr will reflect here after refresh.
                </span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search integrations and workflows..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-muted-foreground" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</option>
              ))}
            </select>
            <select
              value={phaseFilter}
              onChange={(e) => setPhaseFilter(e.target.value)}
              className="px-3 py-2 border border-input rounded-md bg-background text-foreground"
            >
              <option value="">All Phases</option>
              {phases.map(phase => (
                <option key={phase} value={phase}>{phase.charAt(0).toUpperCase() + phase.slice(1)}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Integrations List */}
      <div className="space-y-4">
        {filteredIntegrations.map((integration) => {
          const VendorIconComponent = integration.vendor ? getVendorIcon(integration.vendor) : integration.icon
          const Icon = VendorIconComponent

          return (
            <Card key={integration.id} className="overflow-hidden">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`p-3 rounded-lg ${getCategoryColor(integration.category)} flex items-center justify-center`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-foreground">
                          {integration.name}
                        </h3>
                        {getStatusBadge(integration.status)}
                        {integration.vectrWorkflow && (
                          <div className="flex items-center gap-2">
                            <a
                              href={`https://vectr.ai/workflows/${integration.vectrWorkflow.workflow_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 transition-all"
                            >
                              <VectrLogo size="sm" className="mr-1" />
                              Vectr.ai
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </a>
                            {getWorkflowStatusBadge(integration.vectrWorkflow.workflow_status)}
                          </div>
                        )}
                        <span className={`text-xs px-2 py-1 rounded-full ${getCategoryColor(integration.category)}`}>
                          {integration.category}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${getPhaseColor(integration.phase)}`}>
                          {integration.phase}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {integration.description}
                      </p>
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <div>
                          <span className="font-medium">Trigger (from Vectr):</span> {integration.trigger}
                        </div>
                        <div>
                          <span className="font-medium">Cadence (from Vectr):</span> {integration.cadence}
                        </div>
                        {integration.vectrWorkflow && (
                          <>
                            <div>
                              <span className="font-medium">Vectr Workflow ID:</span> {integration.vectrWorkflow.workflow_id}
                            </div>
                            <div>
                              <span className="font-medium">Webhook URL:</span>{' '}
                              <code className="px-1 py-0.5 bg-secondary rounded text-xs">
                                /webhook/{integration.vectrWorkflow.workflow_id}
                              </code>
                            </div>
                          </>
                        )}
                      </div>
                      {integration.widgetConfig?.enabled && (
                        <div className="flex items-center gap-2 text-xs text-purple-600 dark:text-purple-400 mt-2">
                          <Globe className="h-3 w-3" />
                          <span>Widget Enabled ({integration.widgetConfig.widgetId})</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {integration.vectrWorkflow ? (
                      <>
                        {integration.vectrWorkflow.workflow_status === 'published' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => vectrUnavailable
                              ? toast.error('Vectr unavailable')
                              : handleRedeployWorkflow(integration.id)
                            }
                            disabled={vectrUnavailable}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Redeploy
                          </Button>
                        )}
                        {integration.vectrWorkflow.workflow_status === 'unpublished' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => vectrUnavailable
                              ? toast.error('Vectr unavailable')
                              : handlePublishWorkflow(integration.id)
                            }
                            disabled={vectrUnavailable}
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Publish
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => vectrUnavailable
                            ? toast.error('Vectr unavailable')
                            : handleTestIntegration(integration.id)
                          }
                          disabled={vectrUnavailable || isTesting === integration.id}
                        >
                          {isTesting === integration.id ? (
                            <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                          ) : (
                            <TestTube className="h-4 w-4 mr-2" />
                          )}
                          Test
                        </Button>
                        <a
                          href={`https://vectr.ai/workflows/${integration.vectrWorkflow.workflow_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button variant="outline" size="sm">
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View in Vectr
                          </Button>
                        </a>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => vectrUnavailable
                            ? toast.error('Vectr unavailable')
                            : handleDeleteWorkflow(integration.id)
                          }
                          disabled={vectrUnavailable}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => vectrUnavailable
                            ? toast.error('Vectr unavailable')
                            : handleCreateVectrWorkflow(integration.id)
                          }
                          disabled={vectrUnavailable || creatingWorkflow}
                        >
                          {creatingWorkflow ? (
                            <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                          ) : (
                            <Plus className="h-4 w-4 mr-2" />
                          )}
                          Create in Vectr
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => vectrUnavailable
                            ? toast.error('Vectr unavailable')
                            : handleTestIntegration(integration.id)
                          }
                          disabled={vectrUnavailable || isTesting === integration.id}
                        >
                          {isTesting === integration.id ? (
                            <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                          ) : (
                            <TestTube className="h-4 w-4 mr-2" />
                          )}
                          Test
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          )
        })}
      </div>

      {filteredIntegrations.length === 0 && (
        <Card className="p-12 text-center">
          <AlertCircle className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold text-foreground mb-2">
            No integrations found
          </h2>
          <p className="text-muted-foreground">
            {searchQuery || categoryFilter || phaseFilter ? 'Try adjusting your filters.' : 'No integrations available.'}
          </p>
        </Card>
      )}

      {/* Create Workflow Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                  <Workflow className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                  Create New Vectr Workflow
                </h2>
                <Button variant="outline" onClick={() => {
                  setShowCreateModal(false)
                  setNewWorkflowPrompt('')
                }}>
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Workflow Prompt
                  </label>
                  <textarea
                    value={newWorkflowPrompt}
                    onChange={(e) => setNewWorkflowPrompt(e.target.value)}
                    rows={6}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground"
                    placeholder="Describe the workflow you want to create. For example: 'Create a workflow that enriches property data using Zillow API when an address is saved.'"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Vectr.ai will use AI to generate and configure the workflow based on your prompt.
                  </p>
                </div>
                <div className="pt-4 border-t border-border flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreateModal(false)
                      setNewWorkflowPrompt('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleCreateVectrWorkflow('custom')}
                    disabled={creatingWorkflow || !newWorkflowPrompt.trim()}
                  >
                    {creatingWorkflow ? (
                      <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Create Workflow
                  </Button>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
